import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { SITE } from "@/lib/site";
import { hreflangLinks } from "@/lib/hreflang";
import {
  pageSchemaScript,
  PERSON_ID,
  PORTRAIT_ID,
  SITE_URL,
  PRIMARY_PORTRAIT_PATH,
  PRIMARY_PORTRAIT_WIDTH,
  PRIMARY_PORTRAIT_HEIGHT,
} from "@/seo/schema";

const PATH = "/elv-consultant-in-india";
const URL = `${SITE_URL}${PATH}`;
const TITLE = "ELV & Building Technology Consultant in India | Arif Khan";
const DESC =
  "Arif Khan brings 23+ years across security, perimeter protection, automation, AV and connected building systems, planning ELV technology together in India.";

const FAQS = [
  {
    q: "What is an ELV consultant?",
    a: "An ELV consultant helps plan a building's extra-low-voltage technology systems, such as security, networking, automation and audio-video. The role covers defining requirements, planning how systems connect and coordinating their infrastructure with the architecture and other services.",
  },
  {
    q: "What systems are included under ELV?",
    a: "ELV usually covers systems such as CCTV, access control, intrusion detection, video intercom, structured cabling, networking and Wi-Fi, automation and audio-video. The exact scope varies from project to project.",
  },
  {
    q: "When should an ELV consultant be appointed on a project?",
    a: "Ideally at concept or early design stage, before electrical layouts, ceilings, joinery, network architecture and equipment locations are frozen.",
  },
  {
    q: "What is the difference between an ELV consultant and an MEP consultant?",
    a: "An MEP consultant designs mechanical, electrical and plumbing services such as power, HVAC and water. An ELV consultant focuses on the low-voltage technology systems that sit alongside those services, and works with the MEP team so power, pathways and space are provided for them.",
  },
  {
    q: "What is the difference between ELV consulting and building technology consulting?",
    a: "ELV consulting traditionally focuses on extra-low-voltage systems and their infrastructure. Building technology consulting looks more broadly at how security, connectivity, automation and AV work together for the people using the building. The terms overlap, and many projects need both views.",
  },
  {
    q: "Should security, automation, networking and AV be designed together?",
    a: "Yes, in most projects. These systems often share the network, cabling, racks, power and ceiling space. Planning them together reduces conflicts between vendors and makes integration simpler.",
  },
  {
    q: "Can an ELV consultant also coordinate implementation?",
    a: "It depends on the engagement. Arif Khan works with the Altima Global integration team through implementation, testing and commissioning, and his involvement may cover consulting, design, coordination and implementation oversight.",
  },
  {
    q: "Why should architects plan ELV systems before interiors and electrical work are finalised?",
    a: "Because conduits, power points, rack spaces, camera and speaker positions, access-controlled doors and control locations must be built into drawings. Adding them after ceilings, joinery and finishes are complete usually means rework and compromised performance.",
  },
];

export const Route = createFileRoute("/elv-consultant-in-india")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(PATH)],
    scripts: [
      pageSchemaScript(
        {
          path: PATH,
          type: "WebPage",
          name: TITLE,
          description: DESC,
          breadcrumb: [{ name: "ELV & Building Technology Consultant in India" }],
          extra: {
            about: [{ "@id": PERSON_ID }, { "@id": `${URL}#service` }],
            mainEntity: { "@id": PERSON_ID },
            primaryImageOfPage: { "@id": PORTRAIT_ID },
          },
        },
        [
          {
            "@type": "Service",
            "@id": `${URL}#service`,
            name: "ELV and building technology planning",
            serviceType: "ELV consulting",
            provider: { "@id": PERSON_ID },
            areaServed: { "@type": "Country", name: "India" },
            url: URL,
          },
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      ),
    ],
  }),
});

const PROOF = [
  { value: "23+ Years", label: "Security & building technology experience" },
  { value: "1,000+ Projects", label: "Projects" },
  { value: "500 km+", label: "Perimeter security experience" },
];
const PROOF_LINE = "23+ Years · 1,000+ Projects · 500 km+ Perimeter Security";

const INPUTS = ["Architecture", "MEP", "IT", "Security", "Automation", "AV"];
const SYSTEMS = ["Surveillance", "Access control", "Intrusion detection", "Video intercom", "Networking & Wi-Fi", "Smart automation", "Audio-video", "Meeting-room technology", "Home cinema", "Command & control interfaces"];
const SHARED = ["Network", "Cabling", "Conduits", "Equipment racks", "Power", "UPS", "Ceiling space", "Wall boxes", "Control rooms", "Equipment locations"];

type L = { label: string; to: "sol" | "page"; slug?: string; href?: "/perimeter-security-expert-in-india" };
const EXPERTISE: { t: string; b: string; links: L[] }[] = [
  { t: "Electronic security", b: "CCTV, access control, intrusion detection, video intercom and command/control planned as one system, so events, video and entry records work together.", links: [{ label: "Security Infrastructure", to: "sol", slug: "security-infrastructure" }, { label: "CCTV & Command Centre", to: "sol", slug: "cctv-command-centre" }] },
  { t: "Perimeter protection", b: "Detecting intrusion at the boundary and verifying it with cameras before anyone reaches the building.", links: [{ label: "Perimeter Security Expert in India", to: "page", href: "/perimeter-security-expert-in-india" }] },
  { t: "Networking & Wi-Fi", b: "Security, automation and AV increasingly run on the network. Planning structured cabling, network provisions and Wi-Fi coverage alongside those systems keeps them reliable.", links: [] },
  { t: "Smart home & building automation", b: "Lighting, curtains and blinds, climate, security interfaces, AV control, scenes and simple user interfaces, integrated around how people use the space.", links: [{ label: "Smart Automation", to: "sol", slug: "smart-automation" }] },
  { t: "Professional audio-video", b: "Corporate AV, meeting and presentation spaces and auditoriums, with the displays, audio and control coordinated into the room design.", links: [{ label: "Enterprise AV", to: "sol", slug: "enterprise-av" }, { label: "Auditorium AV", to: "sol", slug: "auditorium-av" }] },
  { t: "Luxury home cinema & residential AV", b: "Dedicated home cinemas, multi-room audio and residential AV planned with the room's architecture and interiors.", links: [{ label: "Luxury Home Cinema", to: "sol", slug: "luxury-home-cinema" }] },
];

const PROVISIONS = ["Conduits", "Cable pathways", "Power points", "UPS requirements", "Equipment racks", "Network cabinets", "Ceiling coordination", "Camera locations", "Access-control door provisions", "Speakers", "Displays", "Sensors", "Wi-Fi access points", "Touch panels", "Curtain-motor provisions", "Control-room locations"];

const WHEN = ["New luxury residence or farmhouse", "Corporate headquarters", "Office fit-out", "Industrial facility", "Institutional or government project", "Auditorium", "Major renovation", "Projects with several technology vendors", "Architect and MEP teams needing a coordinated technology brief"];

const MISTAKES = [
  "Bringing technology vendors in after interiors are largely complete.",
  "Treating security, automation, AV and networking as unrelated packages.",
  "Inadequate conduit or cable pathways.",
  "Undersized equipment racks.",
  "Poor equipment-room ventilation.",
  "Inadequate network planning.",
  "Planning Wi-Fi on internet speed rather than coverage and capacity.",
  "Choosing camera positions after the architecture is finalised.",
  "Missing access-control needs during door-hardware selection.",
  "Missing automation provisions before electrical installation.",
  "AV equipment locations conflicting with interiors or lighting.",
  "No shared documentation or coordination between vendors.",
];

const STAGES = [
  { n: "01", t: "Understand", b: "The building, its users, operational needs, architecture and technology expectations." },
  { n: "02", t: "Plan", b: "The required systems, architecture, infrastructure and integration requirements." },
  { n: "03", t: "Coordinate", b: "With architects, interior designers, electrical/MEP consultants, IT teams, contractors and other stakeholders." },
  { n: "04", t: "Implement & verify", b: "Working with the integration team through installation, programming, testing, commissioning and handover as required." },
];

const TYPES = [
  { t: "Luxury homes & farmhouses", b: "Security, automation, Wi-Fi, AV and home cinema coordinated with architecture and interiors.", slug: "luxury-residences" },
  { t: "Corporate offices & headquarters", b: "Security, networking and collaborative AV that work across floors and meeting spaces.", slug: "corporate-offices" },
  { t: "Industrial facilities", b: "Security, perimeter, access and networking shaped around operations.", slug: "industrial-facilities" },
  { t: "Government & infrastructure", b: "Integrated security and technology coordination across large or distributed sites.", slug: "government-infrastructure" },
  { t: "Auditoriums & presentation spaces", b: "Professional AV, control and coordination with acoustics and room design.", slug: "education-auditoriums" },
  { t: "Institutions & campuses", b: "Security, networking, access and AV across multiple buildings and users.", slug: "education-auditoriums" },
];

const COORD = ["Drawing coordination", "Ceiling locations", "Wall provisions", "Equipment racks", "Conduits", "Network requirements", "Power", "Lighting interfaces", "Door hardware", "Camera positions", "AV equipment", "Automation interfaces", "Control locations"];

function Section({ id, title, children, alt }: { id: string; title: string; children: React.ReactNode; alt?: boolean }) {
  return (
    <section aria-labelledby={id} className={`py-20 md:py-24 ${alt ? "border-y border-border bg-surface" : ""}`}>
      <div className="container-page">
        <h2 id={id} className="text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">{title}</h2>
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

function ExpLink({ l }: { l: L }) {
  const cls = "inline-flex items-center gap-1 text-sm font-medium text-primary";
  const inner = <>{l.label} <ArrowRight className="h-4 w-4" aria-hidden /></>;
  if (l.to === "page" && l.href) return <Link to={l.href} className={cls}>{inner}</Link>;
  return <Link to="/solutions/$slug" params={{ slug: l.slug! }} className={cls}>{inner}</Link>;
}

function Page() {
  return (
    <PageShell>
      <section className="relative pt-40 pb-20 border-b border-border bg-surface">
        <div className="container-page grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow">ELV &amp; Building Technology · India</span>
            <h1 className="mt-5 text-balance text-5xl md:text-6xl font-semibold tracking-tight leading-[0.95]">
              ELV &amp; Building Technology Consultant in India
            </h1>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-2xl">
              Arif Khan has 23+ years of experience across electronic security, perimeter protection, smart automation, professional audio-video and connected building systems in India. He helps project teams plan these systems together: assessing requirements, shaping the system architecture, guiding technology selection and design coordination, then working with the integration team through implementation, testing and commissioning.
            </p>
            <p className="mt-3 text-sm text-ink-soft">BU Head, Security &amp; Automation, Altima Global · Projects across India</p>
            <p className="mt-2 text-sm font-medium">{PROOF_LINE}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">
                Discuss a Building Technology Project <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link to="/for-architects" className="tap-target inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium">
                For Architects &amp; Consultants
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={PRIMARY_PORTRAIT_PATH}
              alt="Arif Khan, ELV and building technology consultant, BU Head, Security & Automation at Altima Global"
              width={PRIMARY_PORTRAIT_WIDTH}
              height={PRIMARY_PORTRAIT_HEIGHT}
              loading="eager"
              decoding="async"
              className="w-full aspect-[4/5] object-cover object-[62%_30%] rounded-3xl border border-border"
            />
          </div>
        </div>
      </section>

      <Section id="credibility" title="Experience at a glance">
        <dl className="grid gap-6 sm:grid-cols-3">
          {PROOF.map((s) => (
            <div key={s.value} className="rounded-2xl border border-border p-6">
              <dd className="text-4xl font-semibold tracking-tight">{s.value}</dd>
              <dt className="mt-2 text-sm text-ink-soft">{s.label}</dt>
            </div>
          ))}
        </dl>
      </Section>

      <Section id="what" title="What does an ELV consultant do?" alt>
        <div className="max-w-3xl space-y-4 text-lg text-ink-soft">
          <p>ELV stands for Extra-Low Voltage. It covers many of the technology systems that make a modern building secure, connected, automated and easier to operate, such as cameras, door access, networks, automation and audio-video. The exact scope varies by project.</p>
          <p>An ELV or building technology consultant helps define what those systems need to do, coordinates them with the architecture and MEP services, guides the choice of technology and reduces conflicts before construction or interiors are completed.</p>
        </div>
      </Section>

      <Section id="together" title="Why building technology should be planned together">
        <p className="max-w-3xl text-lg text-ink-soft">Systems that look separate on a drawing usually share the same infrastructure. When each vendor decides independently, the clashes appear later, on site.</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2 max-w-5xl">
          <div className="rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold">Systems</h3>
            <ul className="mt-4 flex flex-wrap gap-2">{SYSTEMS.map((s) => <li key={s} className="rounded-full border border-border px-3 py-1 text-sm">{s}</li>)}</ul>
          </div>
          <div className="rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold">Shared infrastructure</h3>
            <ul className="mt-4 flex flex-wrap gap-2">{SHARED.map((s) => <li key={s} className="rounded-full border border-border px-3 py-1 text-sm">{s}</li>)}</ul>
          </div>
        </div>
        <figure className="mt-10 max-w-5xl" aria-label="Architecture, MEP, IT, security, automation and AV combine into a coordinated building technology plan">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <ul className="flex flex-wrap gap-2 md:flex-1">
              {INPUTS.map((i, idx) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <span className="rounded-full border border-border px-3 py-1">{i}</span>
                  {idx < INPUTS.length - 1 && <span className="text-ink-soft" aria-hidden>+</span>}
                </li>
              ))}
            </ul>
            <ArrowRight className="h-5 w-5 text-primary rotate-90 md:rotate-0 shrink-0" aria-hidden />
            <div className="rounded-2xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground">Coordinated Building Technology Plan</div>
          </div>
        </figure>
      </Section>

      <Section id="expertise" title="Core areas of expertise" alt>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {EXPERTISE.map((e) => (
            <div key={e.t} className="rounded-2xl border border-border bg-background p-6">
              <h3 className="text-lg font-semibold">{e.t}</h3>
              <p className="mt-3 text-ink-soft">{e.b}</p>
              {e.links.length > 0 && <div className="mt-4 flex flex-col gap-2">{e.links.map((l) => <ExpLink key={l.label} l={l} />)}</div>}
            </div>
          ))}
        </div>
      </Section>

      <Section id="before" title="ELV planning before construction">
        <p className="max-w-3xl text-lg text-ink-soft">Many technology requirements have to be built into the design stage. Fixing them after ceilings, joinery, finishes or civil work are complete usually means rework and weaker system performance.</p>
        <ul className="mt-6 flex flex-wrap gap-3 max-w-5xl">
          {PROVISIONS.map((p) => <li key={p} className="rounded-full border border-border px-4 py-2 text-sm">{p}</li>)}
        </ul>
      </Section>

      <Section id="when" title="When should an ELV / building technology consultant be involved?" alt>
        <ul className="grid gap-3 sm:grid-cols-2 max-w-4xl list-disc pl-5 text-ink-soft">
          {WHEN.map((w) => <li key={w}>{w}</li>)}
        </ul>
        <p className="mt-6 max-w-3xl font-medium">Ideally, building-technology planning should begin before electrical layouts, ceilings, joinery, network architecture and equipment locations are frozen.</p>
      </Section>

      <Section id="roles" title="Consulting, design & execution">
        <p className="max-w-3xl text-lg text-ink-soft">The process starts with the project requirement rather than a preferred product or brand. Technology and equipment are selected after the functional, architectural and infrastructure requirements are understood.</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2 max-w-5xl">
          <div className="rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold">Consulting &amp; Design Coordination</h3>
            <ul className="mt-3 list-disc pl-5 text-ink-soft space-y-1">
              <li>Requirements</li><li>System architecture</li><li>Technology selection</li><li>Layout coordination where applicable</li><li>Infrastructure requirements</li><li>Scope definition</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold">Integration &amp; Execution</h3>
            <ul className="mt-3 list-disc pl-5 text-ink-soft space-y-1">
              <li>Supply</li><li>Installation</li><li>Programming</li><li>System integration</li><li>Testing</li><li>Commissioning</li><li>Handover</li>
            </ul>
          </div>
        </div>
        <p className="mt-6 max-w-3xl text-ink-soft">Arif Khan leads the technology-planning and system-design approach and works with the Altima Global integration team through implementation, testing and commissioning. Depending on the project, his involvement may cover consulting, design, coordination and implementation oversight.</p>
      </Section>

      <Section id="mistakes" title="Common building technology planning mistakes" alt>
        <ul className="grid gap-3 md:grid-cols-2 max-w-5xl">
          {MISTAKES.map((m) => <li key={m} className="rounded-xl border border-border bg-background p-4 text-ink-soft">{m}</li>)}
        </ul>
      </Section>

      <Section id="method" title="Arif Khan's building technology approach">
        <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STAGES.map((s) => (
            <li key={s.n} className="rounded-2xl border border-border p-6">
              <span className="eyebrow">{s.n}</span>
              <h3 className="mt-2 text-lg font-semibold">{s.t}</h3>
              <p className="mt-3 text-ink-soft">{s.b}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section id="types" title="Project types" alt>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {TYPES.map((a) => (
            <div key={a.t} className="rounded-2xl border border-border bg-background p-6">
              <h3 className="text-lg font-semibold">{a.t}</h3>
              <p className="mt-3 text-ink-soft">{a.b}</p>
              <Link to="/industries/$slug" params={{ slug: a.slug }} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">Sector page <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            </div>
          ))}
        </div>
      </Section>

      <Section id="work" title="Selected project experience">
        {/* [PROOF NEEDED] INS Rajali Naval Air Station: omitted until name, scope and permission to reference are confirmed. */}
        {/* [PROOF NEEDED] International Centre for Performing Arts & Culture, Shillong (auditorium AV): omitted until scope and permission to reference are confirmed. */}
        <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
          <div className="rounded-2xl border border-border p-6">
            <span className="eyebrow">Luxury Residences · Delhi NCR</span>
            <h3 className="mt-2 text-lg font-semibold">Residences &amp; farmhouses for HNI clients</h3>
            <p className="mt-3 text-ink-soft">Multi-year engagements covering estate security and automation, planned before interiors are frozen.</p>
          </div>
          <div className="rounded-2xl border border-border p-6">
            <span className="eyebrow">More work</span>
            <h3 className="mt-2 text-lg font-semibold">Projects portfolio</h3>
            <p className="mt-3 text-ink-soft">See engagements across security, automation and AV.</p>
            <Link to="/projects" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">View projects <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          </div>
        </div>
      </Section>

      <Section id="architects" title="For architects, interior designers & MEP consultants" alt>
        <p className="max-w-3xl text-lg text-ink-soft">Arif Khan helps project teams settle technology requirements while they are still lines on a drawing, before they turn into site problems. Typical coordination points:</p>
        <ul className="mt-6 flex flex-wrap gap-3 max-w-5xl">
          {COORD.map((c) => <li key={c} className="rounded-full border border-border bg-background px-4 py-2 text-sm">{c}</li>)}
        </ul>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Discuss Design Coordination <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          <Link to="/for-architects" className="tap-target inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium">For Architects</Link>
        </div>
      </Section>

      <Section id="elv-vs-bt" title="ELV vs building technology consulting">
        <div className="max-w-3xl space-y-4 text-lg text-ink-soft">
          <p>ELV consulting traditionally focuses on extra-low-voltage systems such as security, communications and related technology infrastructure. Building technology is a broader way of looking at how security, connectivity, automation and AV work together within the building.</p>
          <p>Neither term is more correct; they describe overlapping views. Arif Khan's work spans both: the infrastructure detail of ELV and the whole-building view of how those systems serve the people using it.</p>
        </div>
      </Section>

      <Section id="faq" title="Frequently asked questions" alt>
        <div className="max-w-3xl divide-y divide-border border-y border-border">
          {FAQS.map((f) => (
            <div key={f.q} className="py-6">
              <h3 className="text-lg font-semibold">{f.q}</h3>
              <p className="mt-2 text-ink-soft">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="related" title="Related expertise">
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl">
          <li><Link to="/security-consultant-in-india" className="text-primary">Security Consultant in India</Link></li>
          <li><Link to="/perimeter-security-expert-in-india" className="text-primary">Perimeter Security Expert in India</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "security-infrastructure" }} className="text-primary">Security Infrastructure</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "perimeter-protection" }} className="text-primary">Perimeter Protection</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "smart-automation" }} className="text-primary">Smart Automation</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "auditorium-av" }} className="text-primary">Auditorium AV</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "enterprise-av" }} className="text-primary">Enterprise AV</Link></li>
          <li><Link to="/solutions/$slug" params={{ slug: "luxury-home-cinema" }} className="text-primary">Luxury Home Cinema</Link></li>
          <li><Link to="/projects" className="text-primary">Projects</Link></li>
          <li><Link to="/for-architects" className="text-primary">For Architects</Link></li>
          <li><Link to="/contact" className="text-primary">Contact</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "smart-automation-versus-smart-gadgets" }} className="text-primary">Smart automation vs smart gadgets</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "cctv-planning-guide-for-architects" }} className="text-primary">CCTV planning guide for architects</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "auditorium-av-planning-guide" }} className="text-primary">Auditorium AV planning guide</Link></li>
          <li><Link to="/ask-arif/$slug" params={{ slug: "how-much-does-home-automation-cost" }} className="text-primary">How much does home automation cost?</Link></li>
        </ul>
      </Section>

      <section aria-labelledby="cta" className="py-24 border-t border-border bg-surface">
        <div className="container-page text-center max-w-3xl">
          <h2 id="cta" className="text-3xl md:text-4xl font-semibold tracking-tight">Planning the technology before the building is finished?</h2>
          <p className="mt-4 text-ink-soft">Early coordination between architecture, MEP, interiors and technology teams prevents infrastructure conflicts and makes systems easier to integrate. Email {SITE.email} or call {SITE.phone}.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Discuss a Building Technology Project <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            <Link to="/contact" className="tap-target inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium">Contact Arif Khan</Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
